import { useMemo, useState, type ReactNode } from 'react';
import { PresentationContext } from '../lib/presentation-context';
import './presentation-scope.css';

/**
 * A logical viewport for embedded UI. Positioners measure in this containing
 * block, so an ancestor's presentation transform is applied exactly once.
 * Scoped overlays are non-modal to the document: other instances stay usable.
 * Dialogs retain native autofocus/return-focus and dismiss when Tab leaves;
 * Base UI's trap-focus mode would aria-hide the other document instances.
 */
export function PresentationScope({
  children,
  contentOverflow = 'clip',
}: {
  children: ReactNode;
  /** Authoring content can cross the logical viewport; overlays always remain bounded. */
  contentOverflow?: 'clip' | 'visible';
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [boundary, setBoundary] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ container, boundary }), [container, boundary]);

  return (
    <PresentationContext.Provider value={value}>
      <div
        ref={setBoundary}
        data-presentation-scope=""
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          contain: 'layout style',
          isolation: 'isolate',
          overflow: contentOverflow,
        }}
      >
        {children}
        <div
          ref={setContainer}
          data-presentation-portals=""
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 50,
            pointerEvents: 'none',
            contain: 'layout style',
            overflow: 'clip',
          }}
        />
      </div>
    </PresentationContext.Provider>
  );
}
